import type { AuditFindingPartial, AuditRule } from "../types";
import { analyzeCellTypes } from "../../../celltypes/analyzer";
import { loadCellTypesConfigSync } from "../../../celltypes/storage";

/**
 * Flag cells whose type signals conflict — e.g., a text value being SUM'd,
 * or a number stored without a date format that's used in EOMONTH.
 *
 * NOTE on scope: the audit engine runs checks per-sheet, so usage inference
 * only sees formulas within the SAME sheet as the cell being analyzed.
 * Cross-sheet references (a formula on Sheet2 pointing at A1 on Sheet1) are
 * not factored in here — those show up only in the standalone "Identify
 * cell types" dialog, which performs a workbook-wide pass.
 */
export const cellTypeConflicts: AuditRule = {
  id: "system.audit.cell-type-conflicts",
  name: "Cell type conflicts",
  description:
    "Cells whose type signals disagree at non-trivial confidence — e.g., text used in numeric contexts, or numbers stored without the expected date format. Same-sheet analysis only; the dialog version is workbook-wide.",
  enabled: true,
  source: "system",
  severity: "warning",
  /**
   * Per-sheet pass: runs analyzeCellTypes on a single-sheet
   * subset so usage inference stays same-sheet (matching the legacy
   * rule's same-sheet scope — the standalone Identify-cell-types
   * dialog is what runs workbook-wide).
   */
  check(sheet, model) {
    const config = loadCellTypesConfigSync();
    const reports = analyzeCellTypes([sheet], model, config);
    const findings: AuditFindingPartial[] = [];
    for (const r of reports) {
      if (!r.conflict) continue;
      const families = summarizeFamilies(r.signals);
      const consensus = r.consensus ?? "unknown";
      findings.push({
        sheetName: r.sheetName,
        address: r.address,
        message: `type conflict: consensus=${consensus}, signals span ${families}`,
      });
    }
    return findings;
  },
};

function summarizeFamilies(signals: { type: string; confidence: string }[]): string {
  const types = new Set<string>();
  for (const s of signals) {
    if (s.confidence === "low") continue;
    types.add(s.type);
  }
  return Array.from(types).join(", ");
}
